import Cookies from 'js-cookie';
import { APP_VERSION } from '@app/utils/config';
import releaseNotesFile from '@app/release-notes.json';

/**
 * The release notes shown in the post-login banner and in About > What's new.
 *
 * The app is a static bundle, so nothing here can be read from git at runtime: the notes
 * are generated by `npm run release` from the commits between two releases and committed
 * as `src/app/release-notes.json`. This module is the only place that file is read.
 */
export interface Release {
  /** x.y.z, matching the package.json version the release was cut from. */
  version: string;
  /** Commit date of the last commit in the release, as YYYY-MM-DD. */
  date: string;
  /** The commit the notes stop at — the baseline the next release counts from. */
  commit: string;
  /** One line per change, newest commit first. */
  notes: string[];
}

/** Newest release first. */
export const releases: Release[] = (releaseNotesFile.releases ?? []) as Release[];

/**
 * The release this build corresponds to.
 *
 * Normally that is the entry matching APP_VERSION. A local dev build has no version at all
 * (`Local Dev`), and a build made after a release but before the next one is ahead of every
 * entry — in both cases the newest release is the closest true answer, and showing it beats
 * showing nothing.
 */
export const currentRelease = (): Release | undefined =>
  releases.find((release) => release.version === APP_VERSION) ?? releases[0];

const SEEN_COOKIE = 'cas_release_seen';
const ONE_YEAR_IN_DAYS = 365;

/**
 * Whether the banner still has something to say. Keyed on the release the user last
 * dismissed rather than a plain "seen" flag, so every new release announces itself once and
 * a user who skips a release still gets the next one.
 */
export const hasUnseenRelease = (): boolean => {
  const release = currentRelease();
  if (!release || !release.notes.length) return false;

  return Cookies.get(SEEN_COOKIE) !== release.version;
};

export const markReleaseSeen = (): void => {
  const release = currentRelease();
  if (!release) return;

  Cookies.set(SEEN_COOKIE, release.version, {
    expires: ONE_YEAR_IN_DAYS,
    sameSite: 'strict',
    // Mirrors themePreference: not sensitive, and hard-coding `secure` would stop it
    // persisting when the app is served over plain http in dev.
    secure: window.location.protocol === 'https:',
  });
};
